import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Copy, Pencil, Plus, Target } from 'lucide-react';
import {
  formatBRL,
  formatMonthLabel,
  type BudgetResponse,
  type CategoriesResponse,
  type CategoryNode,
  type UpsertBudgetBody,
} from '@lira/shared';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { useMonthStore } from '@/stores/month';
import { CategoryIcon, EmptyState, MoneyInput, MoneyText, Skeleton } from '@/components/ui';
import { DonutChart } from '@/components/charts';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/layout/PageHeader';

const REST_COLOR = '#C4CBD8'; // "sem categorização" na rosca (cinza distinto do trilho)

// --- Helpers ----------------------------------------------------------------

interface FlatCat {
  id: string;
  name: string;
  color: string;
  icon: string;
  child: boolean;
}

function flatten(nodes: CategoryNode[]): FlatCat[] {
  const out: FlatCat[] = [];
  for (const node of nodes) {
    if (node.archived) continue;
    out.push({ id: node.id, name: node.name, color: node.color, icon: node.icon, child: false });
    for (const c of node.children) {
      if (c.archived) continue;
      out.push({ id: c.id, name: c.name, color: c.color, icon: c.icon, child: true });
    }
  }
  return out;
}

/** renda × (1 − economia%) em centavos inteiros. */
function spendingBudgetOf(incomeCents: number, savingsPercent: number): number {
  return Math.round((incomeCents * (100 - savingsPercent)) / 100);
}

// --- KPIs (acompanhamento) --------------------------------------------------

function Kpi({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-canvas p-3">
      <p className="text-[12px] text-muted">{label}</p>
      <div className="mt-0.5 text-lg font-bold text-ink">{children}</div>
    </div>
  );
}

function CategoryProgress({
  name,
  color,
  icon,
  spentCents,
  limitCents,
}: {
  name: string;
  color: string;
  icon: string;
  spentCents: number;
  limitCents: number;
}) {
  const over = spentCents > limitCents;
  const pct = limitCents > 0 ? Math.min(100, (spentCents / limitCents) * 100) : spentCents > 0 ? 100 : 0;
  const diff = Math.abs(limitCents - spentCents);

  return (
    <div className="flex items-center gap-3 py-3">
      <CategoryIcon icon={icon} color={color} fallbackText={name} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-ink">{name}</span>
          <span className="shrink-0 text-[13px] tabular-nums text-muted">
            <MoneyText cents={spentCents} className="text-[13px]" /> / {formatBRL(limitCents)}
          </span>
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-border">
          <div
            className={cn('h-full rounded-full transition-[width]', over ? 'bg-expense' : 'bg-income')}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className={cn('mt-1 text-[12px]', over ? 'text-expense' : 'text-muted')}>
          {over ? `Excedeu ${formatBRL(diff)}` : `Resta ${formatBRL(diff)}`}
        </p>
      </div>
    </div>
  );
}

function BudgetTracking({
  budget,
  onEdit,
  onCopy,
  copying,
}: {
  budget: BudgetResponse;
  onEdit: () => void;
  onCopy: () => void;
  copying: boolean;
}) {
  const plannedBalance = budget.incomeCents - budget.plannedCents;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCopy}
          disabled={copying}
          className="flex h-9 items-center gap-2 rounded-full border border-border px-3 text-sm font-medium text-muted transition-colors hover:border-muted hover:text-ink disabled:opacity-60"
        >
          <Copy className="size-4" />
          Copiar do mês anterior
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="flex h-9 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
        >
          <Pencil className="size-4" />
          Editar
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Renda">
          <MoneyText cents={budget.incomeCents} bold className="text-lg" />
        </Kpi>
        <Kpi label="Gastos planejados">
          <MoneyText cents={budget.plannedCents} bold className="text-lg" />
        </Kpi>
        <Kpi label="Balanço planejado">
          <MoneyText cents={plannedBalance} colorBySign bold className="text-lg" />
        </Kpi>
        <Kpi label="Economia planejada">
          <span className="tabular-nums">{budget.savingsPercent}%</span>
        </Kpi>
      </div>

      <div className="rounded-[var(--radius-panel)] bg-surface p-5 shadow-card">
        <h2 className="mb-1 text-sm font-semibold text-ink">Planejado x realizado</h2>
        {budget.categories.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted">
            Nenhuma categoria com limite definido. Toque em “Editar” para adicionar.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {budget.categories.map((c) => (
              <CategoryProgress
                key={c.categoryId}
                name={c.name}
                color={c.color}
                icon={c.icon}
                spentCents={c.spentCents}
                limitCents={c.limitCents}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Wizard -----------------------------------------------------------------

function StepBadge({ step }: { step: 1 | 2 }) {
  return (
    <div className="flex items-center gap-2 text-[13px] font-medium text-muted">
      <span
        className={cn(
          'grid size-6 place-items-center rounded-full text-xs font-bold',
          step === 1 ? 'bg-primary text-white' : 'bg-primary-weak text-primary',
        )}
      >
        1
      </span>
      <span className="h-px w-6 bg-border" />
      <span
        className={cn(
          'grid size-6 place-items-center rounded-full text-xs font-bold',
          step === 2 ? 'bg-primary text-white' : 'bg-canvas text-muted',
        )}
      >
        2
      </span>
    </div>
  );
}

interface WizardInitial {
  incomeCents: number;
  savingsPercent: number;
  limits: Record<string, number>;
}

function BudgetWizard({
  initial,
  saving,
  onCancel,
  onSave,
}: {
  initial: WizardInitial;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: UpsertBudgetBody) => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [incomeCents, setIncomeCents] = useState(initial.incomeCents);
  const [savingsPercent, setSavingsPercent] = useState(initial.savingsPercent);
  const [limits, setLimits] = useState<Record<string, number>>(initial.limits);

  const categoriesQuery = useQuery({
    queryKey: ['categories', 'expense'],
    queryFn: () => apiGet<CategoriesResponse>('/categories?type=expense'),
  });
  const flat = useMemo(
    () => (categoriesQuery.data ? flatten(categoriesQuery.data.categories) : []),
    [categoriesQuery.data],
  );

  const spendingBudget = spendingBudgetOf(incomeCents, savingsPercent);
  const savingsCents = incomeCents - spendingBudget;
  const totalCategorized = Object.values(limits).reduce((s, v) => s + Math.max(0, v), 0);
  const remaining = spendingBudget - totalCategorized;
  const over = remaining < 0;

  const setLimit = (id: string, cents: number) =>
    setLimits((prev) => ({ ...prev, [id]: cents }));

  const donutSlices = useMemo(() => {
    const slices = flat
      .filter((c) => (limits[c.id] ?? 0) > 0)
      .map((c) => ({ id: c.id, label: c.name, value: limits[c.id]!, color: c.color }));
    if (remaining > 0) {
      slices.push({ id: '__rest__', label: 'Sem categorização', value: remaining, color: REST_COLOR });
    }
    return slices;
  }, [flat, limits, remaining]);

  const handleSave = () => {
    const categories = Object.entries(limits)
      .filter(([, cents]) => cents > 0)
      .map(([categoryId, limitCents]) => ({ categoryId, limitCents }));
    onSave({ incomeCents, savingsPercent, categories });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink">
          {step === 1 ? 'Renda e economia' : 'Limites por categoria'}
        </p>
        <StepBadge step={step} />
      </div>

      {step === 1 ? (
        <div className="space-y-5 rounded-[var(--radius-panel)] bg-surface p-5 shadow-card">
          <div>
            <label htmlFor="budget-income" className="mb-1.5 block text-[13px] text-muted">
              Renda do mês
            </label>
            <MoneyInput
              id="budget-income"
              valueCents={incomeCents}
              onChange={setIncomeCents}
              accent="income"
              size="lg"
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label htmlFor="budget-savings" className="text-[13px] text-muted">
                Quanto quer economizar
              </label>
              <span className="text-sm font-semibold tabular-nums text-primary">{savingsPercent}%</span>
            </div>
            <input
              id="budget-savings"
              type="range"
              min={0}
              max={100}
              step={1}
              value={savingsPercent}
              onChange={(e) => setSavingsPercent(Number(e.target.value))}
              className="w-full"
              style={{ accentColor: 'var(--color-primary)' }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-canvas p-3">
              <p className="text-[12px] text-muted">Orçamento de gastos</p>
              <MoneyText cents={spendingBudget} bold className="mt-0.5 block text-lg" />
              <p className="mt-0.5 text-[11px] text-muted">renda × (1 − {savingsPercent}%)</p>
            </div>
            <div className="rounded-lg bg-income-weak p-3">
              <p className="text-[12px] text-muted">Economia</p>
              <MoneyText cents={savingsCents} bold className="mt-0.5 block text-lg text-income" />
              <p className="mt-0.5 text-[11px] text-muted">reserva do mês</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="flex h-10 items-center rounded-full px-4 text-sm font-medium text-muted transition-colors hover:text-ink"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={incomeCents <= 0}
              className="flex h-10 items-center gap-1.5 rounded-full bg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
            >
              Próximo
              <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_1fr]">
          {/* resumo + rosca */}
          <div className="space-y-3 rounded-[var(--radius-panel)] bg-surface p-5 shadow-card">
            <DonutChart
              slices={donutSlices}
              centerLabel={<MoneyText cents={totalCategorized} bold />}
              centerSub="Categorizado"
            />
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted">Orçamento de gastos</span>
                <MoneyText cents={spendingBudget} className="text-sm" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted">Categorizado</span>
                <MoneyText cents={totalCategorized} className="text-sm" />
              </div>
              {over ? (
                <div className="flex items-center gap-1.5 rounded-lg bg-expense-weak px-3 py-2 text-[13px] text-expense">
                  <AlertTriangle className="size-4 shrink-0" />
                  Você planejou {formatBRL(-remaining)} acima do orçamento.
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-muted">Sem categorização</span>
                  <MoneyText cents={remaining} className="text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* lista de categorias */}
          <div className="rounded-[var(--radius-panel)] bg-surface p-5 shadow-card">
            {categoriesQuery.isLoading ? (
              <div className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-11" />
                ))}
              </div>
            ) : flat.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                Nenhuma categoria de despesa cadastrada.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {flat.map((c) => (
                  <li key={c.id} className={cn('flex items-center gap-3 py-2.5', c.child && 'pl-6')}>
                    <CategoryIcon icon={c.icon} color={c.color} size={30} />
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{c.name}</span>
                    <div className="w-32 shrink-0 rounded-lg border border-border px-2.5 py-1.5 focus-within:border-primary">
                      <MoneyInput
                        valueCents={limits[c.id] ?? 0}
                        onChange={(cents) => setLimit(c.id, cents)}
                        aria-label={`Limite de ${c.name}`}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-medium text-muted transition-colors hover:text-ink"
              >
                <ChevronLeft className="size-4" />
                Voltar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex h-10 items-center rounded-full bg-primary px-6 text-sm font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
              >
                {saving ? 'Salvando…' : 'Salvar planejamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Estado vazio -----------------------------------------------------------

function EmptyBudget({
  monthLabel,
  onCreate,
  onCopy,
  copying,
}: {
  monthLabel: string;
  onCreate: () => void;
  onCopy: () => void;
  copying: boolean;
}) {
  return (
    <EmptyState
      icon={Target}
      title={`Sem planejamento para ${monthLabel}`}
      description="Defina sua renda, quanto quer economizar e limites de gasto por categoria para acompanhar o mês."
    >
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover"
        >
          <Plus className="size-4" />
          Definir novo planejamento
        </button>
        <button
          type="button"
          onClick={onCopy}
          disabled={copying}
          className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-6 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:border-muted hover:text-ink disabled:opacity-60"
        >
          <Copy className="size-4" />
          Copiar do mês anterior
        </button>
      </div>
    </EmptyState>
  );
}

// --- Página -----------------------------------------------------------------

export function BudgetsPage() {
  const month = useMonthStore((s) => s.month);
  const queryClient = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);

  // troca de mês fecha o wizard (evita editar com dados de outro mês)
  useEffect(() => setWizardOpen(false), [month]);

  const budgetQuery = useQuery({
    queryKey: ['budget', month],
    queryFn: () => apiGet<BudgetResponse>(`/budgets/${month}`),
  });

  const copyMutation = useMutation({
    mutationFn: () => apiPost(`/budgets/${month}/copy-previous`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['budget', month] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast('Planejamento copiado do mês anterior.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const saveMutation = useMutation({
    mutationFn: (body: UpsertBudgetBody) => apiPut(`/budgets/${month}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['budget', month] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setWizardOpen(false);
      toast('Planejamento salvo.', 'success');
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const budget = budgetQuery.data;
  const wizardInitial: WizardInitial | null = budget
    ? {
        incomeCents: budget.incomeCents,
        savingsPercent: budget.exists ? budget.savingsPercent : 20,
        limits: Object.fromEntries(budget.categories.map((c) => [c.categoryId, c.limitCents])),
      }
    : null;

  return (
    <section className="space-y-5">
      <PageHeader
        title="Orçamentos"
        subtitle="Planeje limites e acompanhe o realizado no mês."
        meta={<span className="text-sm capitalize text-muted">{formatMonthLabel(month)}</span>}
      />

      {budgetQuery.isLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="card" className="h-16" />
            ))}
          </div>
          <Skeleton variant="card" className="h-64" />
        </div>
      )}

      {budgetQuery.isError && (
        <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
          {errorMessage(budgetQuery.error)}
        </p>
      )}

      {budget &&
        (wizardOpen && wizardInitial ? (
          <BudgetWizard
            key={month}
            initial={wizardInitial}
            saving={saveMutation.isPending}
            onCancel={() => setWizardOpen(false)}
            onSave={(body) => saveMutation.mutate(body)}
          />
        ) : budget.exists ? (
          <BudgetTracking
            budget={budget}
            onEdit={() => setWizardOpen(true)}
            onCopy={() => copyMutation.mutate()}
            copying={copyMutation.isPending}
          />
        ) : (
          <EmptyBudget
            monthLabel={formatMonthLabel(month)}
            onCreate={() => setWizardOpen(true)}
            onCopy={() => copyMutation.mutate()}
            copying={copyMutation.isPending}
          />
        ))}
    </section>
  );
}
